import { useState } from "react";
import { Search } from "lucide-react";
import { usePhoneNumberProvisioning } from "@/hooks/phone/usePhoneNumberProvisioning";

interface ProvisioningFormProps {
  genieName: string;
  brand?: string;
  onProvision: (params: {
    phoneNumber: string;
    friendlyName: string;
    countryCode: string;
    phoneType: 'local' | 'tollFree';
  }) => Promise<void>;
  onCancel: () => void;
  isProvisioning: boolean;
}

const ProvisioningForm = ({ genieName, brand = "#4E9CFF", onProvision, onCancel, isProvisioning }: ProvisioningFormProps) => {
  const [friendlyName, setFriendlyName] = useState("");
  const [areaCode, setAreaCode] = useState("");
  const [countryCode, setCountryCode] = useState("US");
  const [selectedNumberObj, setSelectedNumberObj] = useState<{
    phoneNumber: string;
    phoneType?: 'local' | 'tollFree';
  } | null>(null);

  const { availableNumbers, setSelectedNumber, searchNumbers, isSearching, searchError, clearSearch, hasSearched } =
    usePhoneNumberProvisioning();

  const isNANPCountry = countryCode === "US" || countryCode === "CA";
  const areaCodePlaceholder = "e.g., 415";
  const areaCodeHelpText = isNANPCountry
    ? "3-digit area code (optional)"
    : countryCode === "PH"
      ? "Philippine numbers are mobile numbers (+63 9xx). Area code search is not available."
      : "Area code search is not available for this country. Leave empty.";

  const handleSearch = async () => {
    try {
      await searchNumbers({ countryCode, areaCode });
    } catch (error) {
      console.error("Search failed:", error);
    }
  };

  const handleProvision = async () => {
    if (!selectedNumberObj) return;

    await onProvision({
      phoneNumber: selectedNumberObj.phoneNumber,
      friendlyName: friendlyName || `${genieName} Phone`,
      countryCode,
      phoneType: selectedNumberObj.phoneType || 'local',
    });

    clearSearch();
    setFriendlyName("");
    setAreaCode("");
    setSelectedNumberObj(null);
  };

  const handleCancel = () => {
    clearSearch();
    setFriendlyName("");
    setAreaCode("");
    setSelectedNumberObj(null);
    onCancel();
  };

  const ringStyle = { '--tw-ring-color': `${brand}80` } as React.CSSProperties;

  return (
    <div className="border-t border-gray-200 pt-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Phone Number</h3>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Friendly Name</label>
          <input
            type="text"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
            style={ringStyle}
            placeholder={`${genieName} Phone`}
            value={friendlyName}
            onChange={(e) => setFriendlyName(e.target.value)}
            disabled={isProvisioning}
          />
          <p className="text-xs text-gray-500 mt-1">Leave empty to use default: {genieName} Phone</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
            <select
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
              style={ringStyle}
              value={countryCode}
              onChange={(e) => {
                setCountryCode(e.target.value);
                clearSearch();
              }}
              disabled={isSearching}
            >
              <option value="US">United States (+1)</option>
              <option value="CA">Canada (+1)</option>
              <option value="GB">United Kingdom (+44)</option>
              <option value="AU">Australia (+61)</option>
              <option value="NZ">New Zealand (+64)</option>
              <option value="PH">Philippines (+63)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Area Code (Optional)</label>
            <input
              type="text"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
              style={ringStyle}
              placeholder={areaCodePlaceholder}
              value={areaCode}
              onChange={(e) => setAreaCode(e.target.value)}
              disabled={isSearching || !isNANPCountry}
            />
            <p className="text-xs text-gray-500 mt-1">{areaCodeHelpText}</p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleSearch}
          disabled={isSearching}
          className="w-full flex items-center justify-center px-4 py-3 text-white rounded-lg transition-opacity disabled:opacity-50 font-medium"
          style={{ backgroundColor: brand }}
          onMouseEnter={(e) => !isSearching && (e.currentTarget.style.opacity = "0.85")}
          onMouseLeave={(e) => !isSearching && (e.currentTarget.style.opacity = "1")}
        >
          <Search className="h-4 w-4 mr-2" />
          {isSearching ? "Searching..." : "Search Available Numbers"}
        </button>

        {searchError && <div className="text-red-600 text-sm">{searchError.message}</div>}
      </div>

      {availableNumbers.length > 0 && (
        <div className="mt-6">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Available Numbers</h4>
          <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
            {availableNumbers.map((number) => (
              <div
                key={number.phoneNumber}
                onClick={() => setSelectedNumberObj(number)}
                className={`p-3 border-b border-gray-200 cursor-pointer transition-colors ${
                  selectedNumberObj?.phoneNumber === number.phoneNumber ? "border-l-4" : ""
                }`}
                style={
                  selectedNumberObj?.phoneNumber === number.phoneNumber
                    ? { backgroundColor: `${brand}15`, borderLeftColor: brand }
                    : {}
                }
                onMouseEnter={(e) => {
                  if (selectedNumberObj?.phoneNumber !== number.phoneNumber) {
                    e.currentTarget.style.backgroundColor = `${brand}08`;
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedNumberObj?.phoneNumber !== number.phoneNumber) {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }
                }}
              >
                <span className="font-medium">{number.phoneNumber}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {hasSearched && availableNumbers.length === 0 && !isSearching && (
        <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-center text-sm text-amber-900">
            {searchError ? "Unable to fetch phone numbers for this country." : "No available phone numbers found."}
          </p>
          <p className="text-center text-xs text-amber-700 mt-2">
            {isNANPCountry
              ? "Please try a different area code."
              : `Area code search is not supported for ${countryCode}. Search results are limited for this country.`}
          </p>
        </div>
      )}

      <div className="flex justify-end gap-3 mt-6">
        <button
          type="button"
          onClick={handleCancel}
          className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleProvision}
          disabled={!selectedNumberObj || isProvisioning}
          className="px-4 py-2 text-white rounded-lg transition-opacity disabled:opacity-50 font-medium"
          style={{ backgroundColor: brand }}
          onMouseEnter={(e) => !isProvisioning && selectedNumberObj && (e.currentTarget.style.opacity = "0.85")}
          onMouseLeave={(e) => !isProvisioning && selectedNumberObj && (e.currentTarget.style.opacity = "1")}
        >
          {isProvisioning ? "Adding..." : "Add Number"}
        </button>
      </div>
    </div>
  );
};

export default ProvisioningForm;
